/*
 * AimSens — how far a game turns the camera per mouse count, and the same turn
 * expressed in every other game. One owner for /aim-trainer's 3D mode: the camera
 * there rotates by exactly these numbers, and the «same sens elsewhere» line is
 * computed from the same numbers, so the trainer and the converter cannot drift.
 *
 * Why yaw and not «sens × something»: a game turns the view by yaw × sens degrees
 * per raw mouse count. The same hand movement gives the same turn in two games
 * when yaw₁ × sens₁ = yaw₂ × sens₂ — DPI cancels out (it is the same mouse). DPI
 * is needed only to say it in centimetres per full turn (cm/360).
 *
 * Only games whose yaw is a constant agreed on by independent sources are listed
 * (checked 2026-10-01). PUBG and Rainbow Six scale non-linearly or with FOV;
 * Deadlock, Rust and Tarkov have conflicting published constants — for them the
 * page offers «other game: cm/360», which every converter prints.
 *
 * Arena Breakout: Infinite (added 2026-10-23 on a player's request): 0.1023745° per
 * count, measured in the game by mouse-sensitivity.com (forum topic 9372, 2024-08,
 * «still accurate» 2025-02) and read back from its calculator to 7 digits; xbitlabs,
 * flank.gg and capdetail.com print the same 0.1024. Its calculator moves Rust's
 * answer with FOV (90/70) but not ABI's — the hipfire turn does not depend on FOV.
 * dpiconvert's «0.022» is a template it prints for most games, not a measurement.
 * ABI turns vertically at 75 % of horizontal and has no setting to undo it — so
 * `pitch` exists: the camera's vertical turn is pitch × yaw.
 *
 * FOV: games define their slider differently (4:3 horizontal, 16:9 horizontal,
 * fixed). Everything is reduced to the VERTICAL angle, which is what the game
 * keeps when the screen is wider (Hor+), and what our camera uses. FOV changes
 * what the player sees, never the turn per count.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.AimSens = api;
})(typeof window !== 'undefined' ? window : null, function () {
    'use strict';

    var DEG = Math.PI / 180;
    var R43 = 4 / 3;
    var R169 = 16 / 9;

    /* yaw — degrees per count at sens 1 (Fortnite: per 1 %, its slider is a percentage).
       fov.kind: 'fixed' (the game does not let you change it), 'h43' (slider = horizontal
       angle on a 4:3 screen), 'h169' (slider = horizontal angle on 16:9), 'apex' (4:3
       horizontal, but the slider's 110 is really 108.5: cl_fovScale = value/70 with a
       compressed top end). `digits` — how the game's settings field shows the number.
       `pitch` — vertical turn as a share of the horizontal one (absent = 1, the same).
       ABI's FOV slider (75–110, default 90) is taken as 16:9 horizontal, the Unreal
       Engine 4 convention its engine (4.26) follows; on a 16:9 screen every reading of
       a horizontal slider gives the same picture. */
    var GAMES = {
        cs2:      { yaw: 0.022,     digits: 2, fov: { kind: 'fixed', h43: 90 } },
        valorant: { yaw: 0.07,      digits: 3, fov: { kind: 'fixed', h169: 103 } },
        apex:     { yaw: 0.022,     digits: 2, fov: { kind: 'apex', min: 70, max: 110, def: 90 } },
        ow2:      { yaw: 0.0066,    digits: 2, fov: { kind: 'h169', min: 80, max: 103, def: 103 } },
        fortnite: { yaw: 0.005555,  digits: 1, pct: true, fov: { kind: 'fixed', h169: 80 } },
        cod:      { yaw: 0.0066,    digits: 2, fov: { kind: 'h169', min: 60, max: 120, def: 80 } },
        abi:      { yaw: 0.1023745, digits: 2, pitch: 0.75, fov: { kind: 'h169', min: 75, max: 110, def: 90 } },
        custom:   { yaw: null,      digits: 1, fov: { kind: 'h169', min: 60, max: 130, def: 103 } },
    };
    var ORDER = ['cs2', 'valorant', 'apex', 'ow2', 'fortnite', 'cod', 'abi'];

    var LIMITS = { dpiMin: 100, dpiMax: 32000, cmMin: 3, cmMax: 300 };

    function vFromH(hDeg, aspect) {
        return 2 * Math.atan(Math.tan(hDeg * DEG / 2) / aspect) / DEG;
    }
    function hFromV(vDeg, aspect) {
        return 2 * Math.atan(Math.tan(vDeg * DEG / 2) * aspect) / DEG;
    }

    /* Apex: the slider value x maps linearly from 70 → 70° to 110 → 108.5° (4:3 horizontal). */
    function apexH43(x) {
        return 70 + (x - 70) * (38.5 / 40);
    }

    function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

    function game(id) {
        return GAMES[id] || null;
    }

    /* Vertical FOV in degrees for a game and its FOV setting (ignored for fixed games). */
    function verticalFov(id, value) {
        var g = game(id);
        if (!g) return null;
        var f = g.fov;
        if (f.kind === 'fixed') return f.h43 ? vFromH(f.h43, R43) : vFromH(f.h169, R169);
        var x = Number(value);
        if (!isFinite(x)) x = f.def;
        x = clamp(x, f.min, f.max);
        if (f.kind === 'apex') return vFromH(apexH43(x), R43);
        if (f.kind === 'h43') return vFromH(x, R43);
        return vFromH(x, R169);
    }

    /* Horizontal FOV the player sees on a 16:9 screen — for the label next to the slider. */
    function horizontalFov169(id, value) {
        var v = verticalFov(id, value);
        return v == null ? null : hFromV(v, R169);
    }

    /* Validation: returns null when fine, or an error code the page translates. */
    function check(id, sens, dpi) {
        var g = game(id);
        if (!g) return 'game';
        var d = Number(dpi);
        if (!isFinite(d) || d < LIMITS.dpiMin || d > LIMITS.dpiMax) return 'dpi';
        var s = Number(sens);
        if (!isFinite(s) || s <= 0) return 'sens';
        if (id === 'custom' && (s < LIMITS.cmMin || s > LIMITS.cmMax)) return 'cm';
        return null;
    }

    /* Degrees per mouse count. For 'custom' the «sens» is cm/360 itself. */
    function degPerCount(id, sens, dpi) {
        if (check(id, sens, dpi)) return null;
        var g = game(id);
        var s = Number(sens);
        if (id === 'custom') return 360 / ((s / 2.54) * Number(dpi));
        return g.yaw * s;
    }

    /* Vertical turn as a share of the horizontal one: 1 everywhere except ABI (0.75). */
    function pitchRatio(id) {
        var g = game(id);
        return g && g.pitch > 0 ? g.pitch : 1;
    }

    function cm360(dpc, dpi) {
        if (!(dpc > 0) || !(dpi > 0)) return null;
        return 360 / dpc / dpi * 2.54;
    }

    function sensFor(id, dpc) {
        var g = game(id);
        if (!g || !g.yaw || !(dpc > 0)) return null;
        return dpc / g.yaw;
    }

    function round(v, digits) {
        var m = Math.pow(10, digits);
        return Math.round(v * m) / m;
    }

    /* Same turn in every listed game: [{id, sens, text}], plus cm/360 and eDPI. */
    function convert(id, sens, dpi) {
        var dpc = degPerCount(id, sens, dpi);
        if (dpc == null) return null;
        var out = [];
        for (var i = 0; i < ORDER.length; i++) {
            var gid = ORDER[i];
            var g = GAMES[gid];
            var v = sensFor(gid, dpc);
            var r = round(v, g.digits);
            out.push({ id: gid, sens: r, text: r.toFixed(g.digits) + (g.pct ? '%' : '') });
        }
        var cm = cm360(dpc, Number(dpi));
        return {
            degPerCount: dpc,
            cm360: cm,
            cm360Text: cm.toFixed(cm >= 100 ? 0 : 1),
            games: out,
        };
    }

    return {
        GAMES: GAMES,
        ORDER: ORDER,
        LIMITS: LIMITS,
        vFromH: vFromH,
        hFromV: hFromV,
        apexH43: apexH43,
        verticalFov: verticalFov,
        horizontalFov169: horizontalFov169,
        check: check,
        degPerCount: degPerCount,
        pitchRatio: pitchRatio,
        cm360: cm360,
        sensFor: sensFor,
        convert: convert,
    };
});
